import React from 'react';

/**
 * UpgradeStep - Shows MVP plan details and upgrade CTA for free users
 * Displays plan features with pricing and action buttons
 */

interface UpgradeStepProps {
  onUpgrade: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const UpgradeStep: React.FC<UpgradeStepProps> = ({ onUpgrade, onCancel, isLoading }) => {
  const features = [
    'Web Hosting & Custom Domains',
    '2 GB Database Storage',
    '25 GB File Storage',
    'API Request Limit: 1M/month',
    'Advanced Security Features',
  ];

  return (
    <div className="px-6 py-4">
      {/* Title */}
      <h2 className="text-2xl font-bold text-white mb-2">Upgrade to MVP Plan</h2>
      <p className="text-white/70 text-sm mb-4">
        Unlock web hosting and custom domains with our MVP plan
      </p>

      {/* Plan Card */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">MVP Plan</h3>
            <p className="text-white/60 text-sm">Perfect for production apps</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[#22c55e]">$25</div>
            <div className="text-white/60 text-sm">per month</div>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-2">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              {/* Check icon */}
              <svg
                className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-white/90 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 rounded-lg border border-white/20 text-white/90 text-sm hover:bg-white/5 hover:border-white/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onUpgrade}
          disabled={isLoading}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold text-sm border border-[#22c55e]/30 shadow-lg shadow-[#22c55e]/20 hover:from-[#16a34a] hover:to-[#15803d] hover:shadow-[#22c55e]/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Processing...' : 'Upgrade & Continue'}
        </button>
      </div>
    </div>
  );
};

export default UpgradeStep;

